import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { PauseFilled, PlayFilled } from '@landr/maestro';
import { t } from '@lingui/macro';
import { ActionButton } from 'components/ActionButton';
import { withErrorBoundary } from 'error';
import { noop } from 'utils/noop';
import { useDebouncedState } from 'hooks/useDebouncedState/useDebouncedState';
import { usePlayerContext } from 'contexts/PlayerContext/PlayerContext';
import { PlayerButtonProps, UsePlayerButtonProps } from './types';

const usePlayerButton = ({
    keyboardEventConsumerRef,
    loading,
    playing,
    ref,
    stopPropagation,
    totalTime,
    callback,
    onClick,
    togglePlaying,
    ButtonComponent,
}: UsePlayerButtonProps) => {
    const PauseLabel = t`Pause`;
    const PlayLabel = t`Play`;

    const buttonLabel = playing ? PauseLabel : PlayLabel;

    // CALLBACK
    const handleButtonClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            stopPropagation && event.stopPropagation();

            const element = keyboardEventConsumerRef?.current || ref?.current;

            element?.focus();

            togglePlaying();

            onClick(!playing);

            !playing && callback?.(totalTime);
        },
        [
            ref,
            stopPropagation,
            keyboardEventConsumerRef,
            togglePlaying,
            onClick,
            playing,
            callback,
            totalTime,
        ],
    );

    // MEMO
    const CustomButtonComponent = useMemo(() => {
        return ButtonComponent ? (
            <ButtonComponent
                playing={playing}
                isLoading={loading}
                onClick={handleButtonClick}
            />
        ) : null;
    }, [ButtonComponent, playing, loading, handleButtonClick]);

    return { buttonLabel, CustomButtonComponent, handleButtonClick };
};

const PlayerButton = ({
    id,
    ButtonComponent,
    callback,
    stopPropagation = true,
    label,
    onClick = noop,
    isDemo = false,
    keyboardEventConsumerRef,
    tabIndex,
    ...props
}: PlayerButtonProps) => {
    const buttonRef = useRef<HTMLDivElement>(null);
    const { loading, playing, togglePlaying, totalTime, seek } =
        usePlayerContext();

    const loadingDebounced = useDebouncedState(loading);

    // re-init waveform currentTime on mount
    useEffect(() => {
        seek && seek(0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { buttonLabel, CustomButtonComponent, handleButtonClick } =
        usePlayerButton({
            keyboardEventConsumerRef,
            loading: loadingDebounced,
            playing,
            ref: buttonRef,
            stopPropagation,
            totalTime,
            callback,
            onClick,
            togglePlaying,
            ButtonComponent,
        });

    if (ButtonComponent) {
        return CustomButtonComponent;
    }

    return (
        <ActionButton
            ref={buttonRef}
            aria-label={buttonLabel}
            label={buttonLabel}
            icon={playing ? PauseFilled : PlayFilled}
            isLoading={loadingDebounced}
            onClick={handleButtonClick}
            stopPropagation={stopPropagation}
            tabIndex={tabIndex}
            {...props}
        />
    );
};

PlayerButton.displayName = 'PlayerButton';

export default withErrorBoundary<PlayerButtonProps>(PlayerButton, 'minimal');
